import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { GetTestDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/GetTestDto';
import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';

import { TestDownloadLink } from './TestDownloadLink';
import { ShowMonitorButton } from './ShowMonitorButton';

interface TestsProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  enableMultiSelect?: boolean;
  selectedKeys?: string[];
  withoutDownload?: boolean;
  showMonitorButton?: boolean;
  showManualIndicator?: boolean;
}

export const testForGrid = (test: GetTestDto, showMonitorButton?: boolean) => {
  return {
    key: test.id,
    values: {
      'test.lp': test.lp,
      'test.name': test.name,
      'test.description':
        test.description && test.description.length > 150
          ? test.description.slice(0, 150) + '...'
          : test.description,
      'test.fileName': test.fileName,
      'test.identifier': test.identifier,
      'test.onlineOption': test.onlineOption ? 'Tak' : 'Nie',
      'test.creationTs': momencik(test.creationTs),
      'test.needDevice': test.needDevice ? 'Tak' : 'Nie',
      'test.withoutHarmfulFactors': test.withoutHarmfulFactors ? 'Tak' : 'Nie',
      'test.generateSupportingExamination': test.generateSupportingExamination
        ? 'Tak'
        : 'Nie',
      'test.active': test.active ? 'Tak' : 'Nie',
      'test.averageDuration': test.averageDuration
        ? `${Math.round(test.averageDuration / 60)} min`
        : '',
      completed_exams_count: test.completed_exams_count ?? 0,
      'test.manual': test.hasManual ? (
        <OverlayTrigger
          overlay={<Tooltip>Do testu sa zdefiniowane podręczniki</Tooltip>}
        >
          <span>
            <Icon.Book />
          </span>
        </OverlayTrigger>
      ) : null,
      preview: <TestDownloadLink testIdent={test.identifier} />,
      monitor: showMonitorButton ? (
        <ShowMonitorButton testId={test.id} />
      ) : null,
    },
    isInactive: !test.active,
  };
};

export const Test: React.FC<TestsProps> = props => {
  const columns: GridColumn[] = [
    { header: 'Lp.', property: 'test.lp' },
    { header: 'Nazwa', property: 'test.name' },
    { header: 'Identyfikator', property: 'test.identifier' },
    { header: 'Nazwa pliku', property: 'test.fileName' },
    { header: 'Opis', property: 'test.description', noSorting: false },
    {
      header: 'Data utworzenia',
      property: 'test.creationTs',
    },
    {
      header: 'Dopuszcza online',
      property: 'test.onlineOption',
      noSorting: false,
    },
    {
      header: 'Wymaga dodatkowego urządzenia',
      property: 'test.needDevice',
      noSorting: false,
    },
    {
      header: 'Zadawany badanemu, gdy nie ma podanych czynników szkodliwych',
      property: 'test.withoutHarmfulFactors',
      noSorting: false,
    },
    {
      header: 'Generuje badanie pomocnicze',
      property: 'test.generateSupportingExamination',
      noSorting: false,
    },
    {
      header: 'Aktywny',
      property: 'test.active',
      noSorting: false,
    },
    {
      header: 'Śr. czas trwania',
      property: 'test.averageDuration',
      noFilter: true,
    },
  ];
  if (props.showManualIndicator) {
    columns.push({
      header: 'Podręcznik',
      property: 'test.manual',
      noSorting: true,
      noFilter: true,
    });
    if (!props.withoutDownload) {
      columns.push({
        header: 'Pobierz',
        property: 'preview',
        noSorting: true,
      });
      columns.push({
        header: 'Zakończone testy',
        property: 'completed_exams_count',
        noFilter: true,
      });
    }
    if (props.showMonitorButton) {
      columns.push({
        header: 'Monitor',
        property: 'monitor',
        noSorting: true,
      });
    }
  }

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="test.lp"
      availablePageSizes={[10, 20, 50, 100]}
      onRowClick={props.onRowClick}
      emptyGridMessage="Brak testów w systemie"
      refresh={props.refresh}
      enableMultiSelect={props.enableMultiSelect}
      selectedKeys={props.selectedKeys}
      showExport={true}
    />
  );
};
